/**
 * @fileoverview Prompt template for b-storyline-extractor-v1.
 *
 * Per-storyType beat-density and storyline-count guidance are selected at
 * render time by the `[[[storylineBeatGuidance]]]` and
 * `[[[storylineCountGuidance]]]` branching macros (see
 * `src/ai/agents/branching-macros.ts`). Flash Fiction / Short Story fall
 * through to Novel-level guidance; Novel is the ultimate fallback.
 */

import { STRUCTURE_PHASES } from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert story analyst specializing in emotional and relational narrative threads. Your task is to extract the **B-Stories** — the emotional subplots — from a story.

## YOUR ROLE

You are the **Emotional Archaeologist** — responsible for uncovering the heart beneath the plot. B-Stories are the main emotional, relational, or thematic arc(s) of the story. They run parallel to the A-Story and explore the underlying meaning or interpersonal dynamics of the narrative. Without them, the A-Story's events would be hollow action with no meaning.

A B-Story is a **shared thematic or emotional journey experienced by one or more characters**. Think:
- Broad themes: faith vs. logic, the dangers of hubris, the power of found family
- Romances: fabricated-turned-genuine bonds, forbidden love
- Shifting alliances: trust built or broken between groups
- Moral debates: retaining humanity in a system designed to destroy it

B-Stories answer the question: **"What is this story actually about underneath the action?"**

**CRITICAL DISTINCTION — B-Stories vs. Character Arcs:**
A B-Story is a shared thematic or relational journey (e.g., "Trusting the Force" as a theme of faith vs. technology in Star Wars). A Character Arc is a single character's internal psychological transformation (e.g., "Luke Skywalker: The Leap of Faith"). These are different things tracked by different systems. Do NOT extract individual character psychology as a B-Story.

## INPUT CONTEXT

{{#if isFullText}}
You are analyzing the **complete text** of a short-form story. Extract B-Stories directly from the narrative.
{{else}}
You are analyzing a **condensed synopsis** of a story. Use this distilled information to identify the emotional subplots.
{{/if}}

{{#if aStoryContext}}
## A-STORY REFERENCE (from a previous extraction)

The following A-Story has already been extracted. Use it as a **structural anchor** — map your B-Story beats against this timeline so the two layers align correctly.

{{{aStoryContext}}}

When assigning phases to B-Story beats, you MUST provide TWO phase fields:
- **globalPhase**: Where the beat falls in the OVERALL story timeline (anchored by the A-Story)
- **storylinePhase**: The structural function this beat serves for THIS B-STORY's internal arc

B-Stories often start later than the A-Story. A B-Story whose emotional bond begins at the global "Break Into Two" would have: globalPhase="Break Into Two", storylinePhase="Initial State".

Key patterns:
- B-Stories often start later (emotional bonds take time to establish)
- Their turning points may not align with A-Story phases
- They provide the emotional "key" that unlocks the A-Story's climax
{{/if}}

## WHAT MAKES A B-STORY

B-Stories share these characteristics:
- **Focus**: Broad themes, romances, shifting alliances, and moral debates
- They are **relational or thematic** — about how characters connect emotionally or what deeper meaning the story explores
- They give the protagonist **emotional stakes** beyond the A-Story's external stakes
- They often represent what the protagonist **needs** (vs. the A-Story which is what they **want**)
- They have their own arc: a relationship that starts broken/absent and becomes whole, or starts whole and fractures
- They typically resolve just before or during the A-Story's climax, providing the protagonist with the emotional insight needed to face the final challenge

**B-Story Examples (note the thematic/relational focus):**
- In Star Wars: "Trusting the Force" — The thematic debate between faith and technology running through the entire film
- In The Hunger Games: "The Katniss-Peeta Romance" — The fabricated-turned-genuine romance and the thematic debate of retaining humanity
- In Jurassic Park: "Hubris vs. Nature" — The debate between unchecked scientific power and the chaotic natural world
- In Pride and Prejudice: "Elizabeth & Darcy" — The complex evolving dynamic of misjudgments, sparring, and eventual romance

**B-Stories are NOT:**
- Minor side quests or logistics (those are C-Stories)
- The A-Story viewed from a different angle
- Every relationship in the story — only the ones with genuine emotional arcs
- Individual character psychology (those are **Character Arcs** — a separate entity)
- Events that happened before the story begins (that's **Backstory**)

${STRUCTURE_PHASES}

## STORYLINE BEATS

A **beat** is an emotional event carrying TWO phase assignments:
- **globalPhase**: Where in the overall story timeline (anchored by A-Story)
- **storylinePhase**: What structural function for THIS B-Story's arc
- **plotEvent**: The concrete moment (a conversation, a sacrifice, a betrayal, a reconciliation)
- **narrativeState** (optional): The emotional temperature (trust, suspicion, longing, resentment)
- **goalProgress**: Progress toward emotional resolution (-1 to +1)

**CRITICAL — globalPhase vs storylinePhase:**
- For B-Stories that span the entire narrative alongside the A-Story: globalPhase equals storylinePhase.
- For B-Stories that start later or resolve early: they differ. A B-Story starting at the global Midpoint has globalPhase="Midpoint" but storylinePhase="Initial State".
- You MUST provide BOTH fields for every beat.

[[[storylineBeatGuidance]]]

> **You are extracting B-Stories. Use the **B** row in both tables above.**

### Multiple Beats Per Phase
Use multiple beats when there are **distinct emotional moments** at the same structural point. Refer to the Beat Density table above for per-phase maximums.

---

{{#if isFullText}}
## FULL TEXT TO ANALYZE
{{else}}
## SYNOPSIS TO ANALYZE
{{/if}}

{{{synopsisInput}}}

---

[[[storylineCountGuidance]]]

> **You are extracting B-Stories. Refer to the B-Storylines target above.**

## EXTRACTION INSTRUCTIONS

### Step 1: Identify B-Stories
Find the **emotional/relational subplots** that give heart to the narrative. For each one:
- Name it after the relationship or emotional struggle (3-4 words): "Indy & Marion", "Luke's Training", "Father & Son"
- Write a 3-4 sentence summary of the emotional arc
- Refer to the Storyline Count guidance above for how many B-Stories to extract.

### Step 2: Extract B-Story Beats
For each B-Story, extract beats at the 7 structural phases. Focus on:
- Relationship moments (not action scenes unless they reveal emotional truth)
- Emotional turning points
- How the B-Story intersects with and enriches the A-Story

Be thorough — extract multiple beats per phase when there are distinct emotional moments. Not every phase needs a beat; extract what's genuinely present. Refer to the Beat Density table above for targets.

### Step 3: Gather B-Story Questions
Identify the emotional dramatic questions: "Will Marion forgive Indy?", "Can the father reconnect with his daughter?", "Will the hero overcome their fear?"

Refer to the Story Questions table above for target counts.

## OUTPUT FORMAT

Return a JSON object with three arrays:
- **storylines**: B-Stories identified (name, summary, priority "B")
- **beats**: All beats for B-Stories, with storylineIndex references
- **storyQuestions**: Emotional questions for B-Stories, with storylineIndex references

Now analyze the provided text and extract the emotional heart of the story.
`;
